import { readPendingIntent, clearPendingIntent } from '../utils/pendingIntentStorage';
import type { FpSpMessageType, SpMessageSubject } from '../types';

export type UsePendingFirstMessageIntentParams = {
  /**
   * True for an FA/subdelegate's first-ever message to a provider
   * (requiresFirstMessageType) or an SP sender's first message in a
   * conversation (feature 102).
   */
  required: boolean;
  activeWorkspaceOwnerId: string;
  recipientUserId: string;
};

export type UsePendingFirstMessageIntentResult = {
  messageType: FpSpMessageType | null;
  /** SP compose-time subject (feature 102) — set instead of `messageType` for SP senders. */
  spMessageSubject: SpMessageSubject | null;
  /** Required but no still-valid record was found — the gate should always have produced one (spec §3.5). */
  isMissing: boolean;
  /** Call after a successful first send — never on failure, so a retry reuses the same record. */
  consume: () => void;
};

/**
 * Replaces `MessageComposer`'s retired inline picker (ticket 08): reads the
 * `messageType` the launch-gate/modal already resolved before the composer
 * rendered, instead of tracking component-local picker state.
 */
export function usePendingFirstMessageIntent({
  required,
  activeWorkspaceOwnerId,
  recipientUserId,
}: UsePendingFirstMessageIntentParams): UsePendingFirstMessageIntentResult {
  const record = required ? readPendingIntent(activeWorkspaceOwnerId, recipientUserId) : null;

  return {
    messageType: record?.messageType ?? null,
    spMessageSubject: record?.spMessageSubject ?? null,
    isMissing: required && !record,
    consume: () => clearPendingIntent(activeWorkspaceOwnerId, recipientUserId),
  };
}
